import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { getApiBaseUrl } from "../../config";
import { triggerSessionExpired } from "../../utils/sessionManager";
import { baseQueryWithReauth } from "./baseQuery";
import {
  resetMemberRequestsProgress,
  setMemberRequestsLoading,
  setMemberRequestsProgress,
} from "../slices/slice";

// Define our API service
export const memberRegistrationApi = createApi({
  reducerPath: "memberRegistrationApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["MemberRegistration", "Transitions"],
  endpoints: (builder) => ({
    // Get all member registrations
    getMemberRegistrations: builder.query({
      async queryFn(
        { pageSize = 100, nextToken = undefined, page = 1 } = {},
        queryApi,
        extraOptions,
        baseQuery,
      ) {
        const result = await baseQuery({
          url: "registrations",
          params: { pageSize, ...(nextToken ? { nextToken } : {}), page },
        });

        return result;
      },
      providesTags: ["MemberRegistration"],
    }),

        getAllMemberRequests: builder.query({
      async queryFn({ searchQuery = "", createdFrom = "", createdTo = "", page = 1, limit = 100 } = {}, queryApi) {
        queryApi.dispatch(setMemberRequestsLoading(true));
        queryApi.dispatch(setMemberRequestsProgress(0));

        try {
          const response = await axios.get(`${getApiBaseUrl()}/registrations`, {
            params: {
              ...(searchQuery && { search: searchQuery }),
            ...(createdFrom && { createdFrom }),
              ...(createdTo && { createdTo }),
              page,
              limit,
            },
            withCredentials: true,
            signal: queryApi.signal,
            onDownloadProgress: (event) => {
              const ratioProgress =
                typeof event.progress === "number"
                  ? event.progress * 100
                  : null;
              const totalProgress =
                event.total && event.total > 0
                  ? (event.loaded / event.total) * 100
                  : null;
              const progressValue = totalProgress ?? ratioProgress;

              if (typeof progressValue === "number" && Number.isFinite(progressValue)) {
                queryApi.dispatch(
                  setMemberRequestsProgress(Math.min(99, Math.round(progressValue))),
                );
              }
            },
          });

          // Transform the response to ensure proper data structure
          if (response?.data) {
            // Handle both direct array and nested data structure from backend
            let dataArray = [];
            if (Array.isArray(response.data)) {
              dataArray = response.data;
            } else if (response.data.data && Array.isArray(response.data.data)) {
              dataArray = response.data.data;
            } else if (response.data.message === "Success" && response.data.data) {
              dataArray = Array.isArray(response.data.data)
                ? response.data.data
                : [response.data.data];
            }

            const transformedData = {
              ...response.data,
              data: dataArray.map((item, index) => ({
                ...item,
                ROWID: index + 1, // Use sequential numbers 1, 2, 3...
                // Preserve membership_category object from backend; add a name alias for convenience
                membership_category_name: item.membership_category?.name || "N/A",
                CREATEDTIME: item.createdAt,
                applicants_designation: item.applicant_designation,
              })),
            };

            queryApi.dispatch(setMemberRequestsProgress(100));
            return { data: transformedData };
          }

          queryApi.dispatch(setMemberRequestsProgress(100));
          return { data: response.data };
        } catch (error) {
          if (error?.code === "ERR_CANCELED") {
            queryApi.dispatch(resetMemberRequestsProgress());
            return {
              error: {
                status: "CANCELED",
                data: "Request canceled",
              },
            };
          }

          if (error?.response?.status === 401) {
            localStorage.removeItem("user");
            triggerSessionExpired();
          }

          queryApi.dispatch(resetMemberRequestsProgress());
          return {
            error: {
              status: error?.response?.status || "FETCH_ERROR",
              data: error?.response?.data || error?.message || "Request failed",
            },
          };
        } finally {
          queryApi.dispatch(setMemberRequestsLoading(false));
        }
      },
      providesTags: (result, error, arg) => {
        const { searchQuery = "", createdFrom = "", createdTo = "", page = 1 } = arg || {};
        return searchQuery || createdFrom || createdTo || page > 1
          ? [
              {
                type: "MemberRegistration",
                id: `SEARCH-${searchQuery}-${createdFrom}-${createdTo}-${page}`,
              },
            ]
          : ["MemberRegistration"];
      },
    }),

    // Get member registrations by token
    getMemberRegistrationsByTokenOnly: builder.query({
      async queryFn(token, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `requests/token/${token}`,
        });
        return result;
      },
      providesTags: ["MemberRegistration"],
    }),

    // Get a single member registration by ID - FIXED
    getMemberRegistrationById: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/${id}`,
        });

        // The backend returns: { message: "Success", data: {...} }
        // result.data is already that full response
        // So we just return it as-is, no transformation needed
        return result;
      },
      providesTags: (result, error, id) => [
        { type: "MemberRegistration", id },
        "MemberRegistration",
      ],
    }),

    // Get a single member registration by KRA Pin
    getMemberRegistrationByPin: builder.query({
      async queryFn(kraPin, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/query/${kraPin}`,
        });

        return result;
      },
      providesTags: (result, error, id) => [
        { type: "MemberRegistration", id },
        "MemberRegistration",
      ],
    }),
    // Get a single member registration by Company Email
    getMemberRegistrationByEmail: builder.query({
      async queryFn(email, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/query/email/${email}`,
        });

        return result;
      },
      providesTags: (result, error, id) => [
        { type: "MemberRegistration", id },
        "MemberRegistration",
      ],
    }),

    // Get a single member registration by token
    getMemberRegistrationByToken: builder.query({
      async queryFn(token, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/resubmission/${token}`,
        });

        return result.data;
      },
      providesTags: (result, error, id) => [
        { type: "MemberRegistration", id },
        "MemberRegistration",
      ],
    }),

    // Get a single member registration by ID
    getMemberRegistrationByTokenForChangeRequest: builder.query({
      query: (token) => `registrations/change_request/${token}`,
      transformResponse: (response) => {
        // Ensure the response matches the expected structure
        return {
          data: {
            request: response.data.request,
            contactPersons: response.data.contactPersons || [], // Adjust based on backend response
          },
        };
      },
      providesTags: (result, error, token) => [
        { type: "MemberRegistration", id: token },
        "MemberRegistration",
      ],
    }),

    // Create a new member registration
    createRegistration: builder.mutation({
      query: (formData) => ({
        url: "registrations",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["MemberRegistration"],
    }),

    // Update a member registration
    updateMemberRegistration: builder.mutation({
      async queryFn({ id, formData }, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/${id}`,
          method: "PUT",
          body: formData,
        });
        return result;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "MemberRegistration", id },
        "MemberRegistration",
      ],
    }),

    // Update a member registration via token for resubmission
    updateMemberRegistrationByToken: builder.mutation({
      async queryFn(
        { id, token, formData },
        queryApi,
        extraOptions,
        baseQuery,
      ) {
        const result = await baseQuery({
          url: `registrations/resubmission/${token}?id=${id}`,
          method: "PUT",
          body: formData,
        });
        return result;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "MemberRegistration", id },
        "MemberRegistration",
      ],
    }),

    // Delete a member registration
    deleteMemberRegistration: builder.mutation({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/${id}`,
          method: "DELETE",
        });

        return result;
      },

      invalidatesTags: ["MemberRegistration"],
    }),

    // Get available transitions for a member registration
    getAvailableTransitions: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/${id}/transitions`,
        });

        return result;
      },
      providesTags: (result, error, id) => [{ type: "Transitions", id }],
    }),

    // Trigger a status transition
    triggerStatusTransition: builder.mutation({
      async queryFn(
        { registrationId, transitionId, ...patch },
        queryApi,
        extraOptions,
        baseQuery,
      ) {
        const result = await baseQuery({
          url: `registrations/${registrationId}/transition/${transitionId}`,
          method: "POST",
          body: patch,
        });

        return result;
      },
      invalidatesTags: (result, error, { registrationId }) => [
        { type: "MemberRegistration", id: registrationId },
        { type: "Transitions", id: registrationId },
        "MemberRegistration",
      ],
    }),

    // Revert to previous status
    revertMemberRequestStatus: builder.mutation({
      async queryFn({ registrationId }, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/${registrationId}/revert`,
          method: "POST",
        });

        return result;
      },
      invalidatesTags: (result, error, { registrationId }) => [
        { type: "MemberRegistration", id: registrationId },
        { type: "Transitions", id: registrationId },
        "MemberRegistration",
      ],
    }),

    // Reset (terminate + clear) the Zoho contract without changing status
    resetRegistrationContract: builder.mutation({
      async queryFn({ registrationId }, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `registrations/${registrationId}/reset-contract`,
          method: "POST",
        });

        return result;
      },
      invalidatesTags: (result, error, { registrationId }) => [
        { type: "MemberRegistration", id: registrationId },
        "MemberRegistration",
      ],
    }),

    setManualRegistrationStatus: builder.mutation({
      async queryFn(
        { registrationId, targetStatusId, reason = "" },
        queryApi,
        extraOptions,
        baseQuery,
      ) {
        const result = await baseQuery({
          url: `registrations/${registrationId}/manual-status`,
          method: "POST",
          body: { targetStatusId, reason },
        });

        return result;
      },
      invalidatesTags: (result, error, { registrationId }) => [
        { type: "MemberRegistration", id: registrationId },
        { type: "Transitions", id: registrationId },
        "MemberRegistration",
      ],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetMemberRegistrationsQuery,
  useGetAllMemberRequestsQuery,
  useLazyGetAllMemberRequestsQuery,
  useGetMemberRegistrationsByTokenOnlyQuery,
  useGetMemberRegistrationByIdQuery,
  useGetMemberRegistrationByTokenQuery,
  useGetMemberRegistrationByTokenForChangeRequestQuery,
  useGetMemberRegistrationByPinQuery,
  useCreateRegistrationMutation,
  useUpdateMemberRegistrationMutation,
  useUpdateMemberRegistrationByTokenMutation,
  useDeleteMemberRegistrationMutation,
  useGetAvailableTransitionsQuery,
  useTriggerStatusTransitionMutation,
  useRevertMemberRequestStatusMutation,
  useResetRegistrationContractMutation,
  useSetManualRegistrationStatusMutation,
} = memberRegistrationApi;
